import React, { useEffect, useState, useRef, useMemo } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getProjects,
  createProjects,
  updateProjects,
  deleteProjects,
} from '../api/projectsAPI';
import { getCustomers } from '../api/customersAPI';
import { getProjectStatus } from '../api/project-statusAPI';
import Select from 'react-select';
import { HierarchySelect } from './common/HierarchySelect';

const ProjectsLayer = () => {
  const userProfile = useUserProfile();
  const [projectList, setProjectList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [newProject, setNewProject] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchProjects = async () => {
    try {
      const { projects, departments } = await getProjects();
      console.log('projects', projects)
      setProjectList(projects);
      setDepartmentList(departments);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomerList(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchStatuses = async () => {
    try {
      const data = await getProjectStatus();
      setStatusList(data);
    } catch (err) {
      console.error('Error fetching statuses:', err);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    fetchStatuses();
  }, []);

  useEffect(() => {
    if (dtRef.current) dtRef.current.destroy();
    if (projectList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({ pageLength: 25, dom: 'rtip' });
    }
    return () => dtRef.current?.destroy(true);
  }, [projectList]);

  useEffect(() => { dtRef.current?.search(searchTerm).draw(); }, [searchTerm]);

  const validateForm = () => {
    const newErrors = {};
    if (!newProject.name?.trim()) newErrors.name = 'Name is required';
    if (!newProject.customerId) newErrors.customerId = 'Customer is required';
    if (!newProject.status) newErrors.status = 'Status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {};
    for (const key in newProject) {
      if (newProject[key] !== null && typeof newProject[key] !== 'object') {
        payload[key] = newProject[key];
      }
    }
    editingId ? await updateProjects(editingId, payload) : await createProjects(payload);
    setShowModal(false);
    setEditingId(null);
    if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
    setNewProject({});
    await fetchProjects();
  };

  const handleEdit = (item) => {
    setNewProject({
      ...item,
      customerId: item.customer?.id,
      status: item.status2?.id,
      departmentId: item.department?.id ?? null,
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete project?')) return;
    await deleteProjects(id);
    if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
    await fetchProjects();
  };

  const customerOptions = customerList.map(c => ({ value: c.id, label: c.name }));
  const statusOptions = statusList.map(s => ({ value: s.id, label: s.name }));
  const departmentOptions = useMemo(() => {
    if (!Array.isArray(departmentList)) return [];
    return departmentList
      .filter(item => item && item.id !== undefined && item.id !== null)
      .map(item => ({
        value: item.id,
        label: item.name || item.departmentName || item.description || `ID ${item.id}`,
        parentId: item.parentId ?? item.parent?.id ?? null,
        parentName: item.parent?.name ?? item.parentName ?? '',
      }));
  }, [departmentList]);

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between p-3">
        <input type="text" className="form-control w-auto" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        {userProfile?.privileges?.includes('projects:create') && (
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={() => { setNewProject({}); setEditingId(null); setShowModal(true); }}>
            <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Project
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table ref={tableRef} className="table bordered-table mb-0">
            <thead><tr><th>Name</th><th>Customer</th><th>Status</th><th>Department</th><th>Actions</th></tr></thead>
            <tbody>
              {projectList.map(item => (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td>{item.customer?.name}</td>
                  <td>{item.status2?.name}</td>
                  <td>{item.department?.name}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('projects:update') && (
                        <button className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleEdit(item)}>
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('projects:delete') && (
                        <button className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center" onClick={() => handleDelete(item.id)}>
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">{editingId ? 'Edit Project' : 'New Project'}</h5><button className="btn-close" onClick={() => setShowModal(false)} /></div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Name *</label>
                  <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} name="name" value={newProject.name || ''} onChange={handleInputChange} />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Customer *</label>
                  <Select
                    name="customerId"
                    value={customerOptions.find(opt => opt.value === newProject.customerId) || null}
                    onChange={opt => setNewProject(prev => ({ ...prev, customerId: opt?.value }))}
                    options={customerOptions}
                    isClearable
                  />
                  {errors.customerId && <div className="react-select-invalid">{errors.customerId}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status *</label>
                  <Select
                    name="status"
                    value={statusOptions.find(opt => opt.value === newProject.status) || null}
                    onChange={opt => setNewProject(prev => ({ ...prev, status: opt?.value }))}
                    options={statusOptions}
                    isClearable
                  />
                  {errors.status && <div className="react-select-invalid">{errors.status}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Department</label>
                  <HierarchySelect
                    name="departmentId"
                    isMulti={false}
                    options={departmentOptions}
                    value={newProject.departmentId ?? null}
                    onChange={value => setNewProject(prev => ({ ...prev, departmentId: value ?? null }))}
                    isClearable
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" value={newProject.description || ''} onChange={handleInputChange} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              <button className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default ProjectsLayer;
